import * as THREE from 'three'

import { RobloxMaterial } from '../types'

/**
 * Roblox 風マテリアルのプロシージャルテクスチャ。
 *
 * 「素材をかえる」ブロックの効果が roughness/metalness の差だけでは視認
 * できないため、マテリアルごとの模様（木目・レンガ・芝・石畳・錆など）を
 * canvas で生成して albedo マップに載せる。模様は白〜グレーの濃淡で描き、
 * MeshStandardMaterial の map × color 乗算により Part 色がそのまま着色される。
 *
 * looks_materials ドロップダウンの全素材（45 種）を、見分けられる固有の
 * 模様で描き分ける。レンガ系（Brick / Cobblestone / Pebble…）や金属系
 * （Metal / CorrodedMetal / DiamondPlate / Foil）が同一見た目に潰れない
 * ことを優先し、写実性よりも判別性を重視した記号的な模様にする。
 *
 * canvas はマテリアルごとにモジュール内で 1 枚だけ生成して共有し、
 * THREE.CanvasTexture はエントリごとに生成する（repeat がエントリ固有のため）。
 */

/** テクスチャ 1 タイルの解像度（px）。 */
const TILE_SIZE = 128

/** 模様 1 タイルが対応する studs 数。repeat 計算（size / TILE_STUDS）に使う。 */
export const MATERIAL_TILE_STUDS = 4

type Ctx = CanvasRenderingContext2D
type PatternPainter = (context: Ctx, size: number) => void

/** 決定的な擬似乱数（モジュールロードごとに同じ模様を生成する）。 */
const createRandom = (seed: number): (() => number) => {
  let value = seed
  return () => {
    value = (value * 1103515245 + 12345) % 2147483648
    return value / 2147483648
  }
}

/** 無地ベースを塗る。 */
const fill = (context: Ctx, size: number, shade: number): void => {
  context.fillStyle = `rgb(${shade}, ${shade}, ${shade})`
  context.fillRect(0, 0, size, size)
}

/**
 * 細粒ノイズ（砂・コンクリ・アスファルト等）。
 * count 個の dot×dot 矩形を min..max の濃淡で散らす。
 */
const speckle = (
  context: Ctx,
  size: number,
  seed: number,
  count: number,
  min: number,
  max: number,
  dot = 1.5,
): void => {
  const random = createRandom(seed)
  for (let i = 0; i < count; i += 1) {
    const shade = min + Math.round(random() * (max - min))
    context.fillStyle = `rgb(${shade}, ${shade}, ${shade})`
    context.fillRect(random() * size, random() * size, dot, dot)
  }
}

/** 大きめの濃淡ブロッチ（錆・泥・しっくいのムラ等）。 */
const blotch = (
  context: Ctx,
  size: number,
  seed: number,
  count: number,
  minR: number,
  maxR: number,
  shades: readonly number[],
  alpha: number,
): void => {
  const random = createRandom(seed)
  for (let i = 0; i < count; i += 1) {
    const shade = shades[Math.floor(random() * shades.length)]
    context.fillStyle = `rgba(${shade}, ${shade}, ${shade}, ${alpha})`
    context.beginPath()
    context.arc(
      random() * size,
      random() * size,
      minR + random() * (maxR - minR),
      0,
      Math.PI * 2,
    )
    context.fill()
  }
}

/**
 * 矩形タイル（レンガ・タイル・屋根板等）。半ずらし可。
 * rows×cols のタイルを base 色で塗り、gap px の目地（背景色）を残す。
 */
const tiles = (
  context: Ctx,
  size: number,
  rows: number,
  cols: number,
  grout: number,
  tile: number,
  gap: number,
  stagger: boolean,
): void => {
  fill(context, size, grout)
  const rowH = size / rows
  const colW = size / cols
  context.fillStyle = `rgb(${tile}, ${tile}, ${tile})`
  for (let row = 0; row < rows; row += 1) {
    const offset = stagger && row % 2 === 1 ? colW / 2 : 0
    for (let col = -1; col < cols + 1; col += 1) {
      context.fillRect(
        col * colW + offset + gap,
        row * rowH + gap,
        colW - gap * 2,
        rowH - gap * 2,
      )
    }
  }
}

/** ランダム多角形風の丸石（Cobblestone / Pebble）。 */
const stones = (
  context: Ctx,
  size: number,
  seed: number,
  cell: number,
  jitter: number,
  grout: number,
  minShade: number,
  maxShade: number,
): void => {
  fill(context, size, grout)
  const random = createRandom(seed)
  for (let y = 0; y < size; y += cell) {
    for (let x = 0; x < size; x += cell) {
      const shade = minShade + Math.round(random() * (maxShade - minShade))
      context.fillStyle = `rgb(${shade}, ${shade}, ${shade})`
      const r = cell / 2 - 1.5
      context.beginPath()
      context.ellipse(
        x + cell / 2 + (random() - 0.5) * jitter,
        y + cell / 2 + (random() - 0.5) * jitter,
        r * (0.8 + random() * 0.3),
        r * (0.8 + random() * 0.3),
        random() * Math.PI,
        0,
        Math.PI * 2,
      )
      context.fill()
    }
  }
}

/** うねる脈・亀裂線（Marble / CrackedLava / Glacier 等）。 */
const veins = (
  context: Ctx,
  size: number,
  seed: number,
  count: number,
  shade: number,
  alpha: number,
  width: number,
): void => {
  const random = createRandom(seed)
  context.strokeStyle = `rgba(${shade}, ${shade}, ${shade}, ${alpha})`
  context.lineWidth = width
  for (let i = 0; i < count; i += 1) {
    const x = random() * size
    context.beginPath()
    context.moveTo(x, 0)
    context.bezierCurveTo(
      x + (random() - 0.5) * size * 0.6,
      size * 0.33,
      x + (random() - 0.5) * size * 0.6,
      size * 0.66,
      x + (random() - 0.5) * size * 0.4,
      size,
    )
    context.stroke()
  }
}

/** 横ストライプ（Metal ヘアライン・Slate 層・Water 波等）。 */
const bands = (
  context: Ctx,
  size: number,
  seed: number,
  minH: number,
  maxH: number,
  minShade: number,
  maxShade: number,
): void => {
  const random = createRandom(seed)
  let y = 0
  while (y < size) {
    const bandHeight = minH + random() * (maxH - minH)
    const shade = minShade + Math.round(random() * (maxShade - minShade))
    context.fillStyle = `rgb(${shade}, ${shade}, ${shade})`
    context.fillRect(0, y, size, bandHeight)
    y += bandHeight
  }
}

/** 木目（Wood / WoodPlanks）。 */
const woodGrain = (context: Ctx, size: number, seed: number): void => {
  fill(context, size, 255)
  for (let x = 0; x < size; x += 1) {
    const wave = Math.sin(x * 0.22) * 0.5 + Math.sin(x * 0.061) * 0.5
    const shade = 235 + Math.round(wave * 14)
    context.fillStyle = `rgb(${shade}, ${shade}, ${shade})`
    context.fillRect(x, 0, 1, size)
  }
  veins(context, size, seed, 6, 120, 0.35, 1)
}

// --- 各マテリアルの模様 -----------------------------------------------------

const paintWood: PatternPainter = (c, s) => {
  woodGrain(c, s, 7)
}

const paintWoodPlanks: PatternPainter = (c, s) => {
  woodGrain(c, s, 7)
  // 板の継ぎ目（横線）と釘風の点で「板張り」を示す。
  c.fillStyle = 'rgba(90, 90, 90, 0.55)'
  for (let i = 0; i < 4; i += 1) {
    c.fillRect(0, (s / 4) * i, s, 2)
  }
}

const paintCardboard: PatternPainter = (c, s) => {
  // 段ボール: 横方向の波リブ（細い縞）+ うっすら繊維ノイズ。
  fill(c, s, 235)
  c.fillStyle = 'rgb(215, 215, 215)'
  for (let y = 0; y < s; y += 6) {
    c.fillRect(0, y, s, 2)
  }
  speckle(c, s, 163, 300, 220, 245)
}

const paintBrick: PatternPainter = (c, s) => {
  tiles(c, s, 4, 2, 217, 191, 2, true)
}

const paintCeramicTiles: PatternPainter = (c, s) => {
  // 正方タイル + 細い目地。レンガより明るく整然。
  tiles(c, s, 4, 4, 200, 240, 1.5, false)
}

const paintClayRoofTiles: PatternPainter = (c, s) => {
  // 半円の重なり（鱗状）で瓦を示す。
  fill(c, s, 200)
  const rows = 4
  const cols = 4
  const w = s / cols
  const h = s / rows
  c.strokeStyle = 'rgba(110, 110, 110, 0.8)'
  c.lineWidth = 2
  c.fillStyle = 'rgb(225, 225, 225)'
  for (let row = 0; row <= rows; row += 1) {
    const offset = row % 2 === 1 ? w / 2 : 0
    for (let col = -1; col <= cols; col += 1) {
      c.beginPath()
      c.arc(col * w + w / 2 + offset, row * h, w / 2, 0, Math.PI)
      c.fill()
      c.stroke()
    }
  }
}

const paintRoofShingles: PatternPainter = (c, s) => {
  // 互い違いの横長板（こけら板）。瓦より角張る。
  tiles(c, s, 5, 3, 150, 205, 1.5, true)
}

const paintPavement: PatternPainter = (c, s) => {
  // 大きめの敷石グリッド + 粒ノイズ。
  tiles(c, s, 2, 2, 195, 228, 2.5, false)
  speckle(c, s, 29, 500, 200, 240)
}

const paintCobblestone: PatternPainter = (c, s) => {
  stones(c, s, 31, 32, 8, 165, 205, 245)
}

const paintPebble: PatternPainter = (c, s) => {
  stones(c, s, 37, 16, 5, 180, 205, 248)
}

/**
 * 草の葉 1 本（先細りの細長い楕円）を、タイル境界をまたいでも連続するよう
 * 9 近傍（±size オフセット）へ複製して描く。Grass は全シーンの地面で最も
 * 使用数が多く、タイル繰り返しの境界線が見えると芝が格子状に割れて見える。
 */
const grassBlade = (
  context: Ctx,
  size: number,
  x: number,
  y: number,
  angle: number,
  length: number,
  width: number,
  shade: number,
): void => {
  context.fillStyle = `rgb(${shade}, ${shade}, ${shade})`
  for (const offsetX of [-size, 0, size]) {
    for (const offsetY of [-size, 0, size]) {
      context.save()
      context.translate(x + offsetX, y + offsetY)
      context.rotate(angle)
      context.beginPath()
      context.ellipse(0, -length / 2, width, length / 2, 0, 0, Math.PI * 2)
      context.fill()
      context.restore()
    }
  }
}

const paintGrass: PatternPainter = (c, s) => {
  // 本家の Grass は短い草の葉が密集した有機的な見た目。単純な斑点だと
  // 「テクスチャが荒い」印象になるため、影の葉（暗・太）→ハイライトの葉
  // （明・細）の 2 層を重ねて、葉の重なりと奥行きを出す。
  fill(c, s, 200)
  const random = createRandom(11)
  // 下層: 影の葉。
  for (let i = 0; i < 750; i += 1) {
    grassBlade(
      c,
      s,
      random() * s,
      random() * s,
      random() * Math.PI * 2,
      6 + random() * 6,
      1.1 + random() * 0.6,
      150 + Math.round(random() * 40),
    )
  }
  // 上層: ハイライトの葉。
  for (let i = 0; i < 750; i += 1) {
    grassBlade(
      c,
      s,
      random() * s,
      random() * s,
      random() * Math.PI * 2,
      5 + random() * 5,
      0.7 + random() * 0.5,
      210 + Math.round(random() * 45),
    )
  }
}

const paintLeafyGrass: PatternPainter = (c, s) => {
  paintGrass(c, s)
  // 落ち葉風の丸ブロッチを重ねる。
  blotch(c, s, 41, 60, 2, 5, [150, 200, 250], 0.5)
}

const paintConcrete: PatternPainter = (c, s) => {
  fill(c, s, 239)
  speckle(c, s, 13, 1400, 190, 245)
}

const paintLimestone: PatternPainter = (c, s) => {
  // 明るく柔らかい横層。コンクリより整い、スレートより淡い。
  bands(c, s, 43, 10, 22, 225, 248)
  speckle(c, s, 43, 300, 215, 240)
}

const paintGranite: PatternPainter = (c, s) => {
  // 粗い斑点（明暗のコントラスト強め）が花崗岩の特徴。
  fill(c, s, 225)
  speckle(c, s, 47, 700, 120, 250, 3)
}

const paintMarble: PatternPainter = (c, s) => {
  fill(c, s, 248)
  veins(c, s, 53, 5, 150, 0.5, 2)
  veins(c, s, 59, 3, 190, 0.35, 1)
}

const paintBasalt: PatternPainter = (c, s) => {
  // 柱状節理風の暗い縦帯。
  fill(c, s, 150)
  const random = createRandom(61)
  let x = 0
  while (x < s) {
    const w = 14 + random() * 18
    const shade = 120 + Math.round(random() * 60)
    c.fillStyle = `rgb(${shade}, ${shade}, ${shade})`
    c.fillRect(x + 1.5, 0, w - 3, s)
    x += w
  }
}

const paintSlate: PatternPainter = (c, s) => {
  bands(c, s, 17, 6, 20, 195, 245)
}

const paintCrackedLava: PatternPainter = (c, s) => {
  // 暗い地に明るい亀裂（発光はマテリアル設定側の emissive が担う）。
  fill(c, s, 80)
  veins(c, s, 67, 7, 255, 0.85, 2.5)
  veins(c, s, 71, 5, 230, 0.6, 1.2)
}

const paintRock: PatternPainter = (c, s) => {
  // ごつごつした大きめの濃淡。
  fill(c, s, 200)
  blotch(c, s, 73, 90, 5, 14, [150, 175, 225, 240], 0.5)
}

const paintSandstone: PatternPainter = (c, s) => {
  bands(c, s, 79, 16, 30, 222, 242)
  speckle(c, s, 79, 700, 205, 240)
}

const paintGround: PatternPainter = (c, s) => {
  // 土: 粗ノイズ + 小石。
  fill(c, s, 215)
  speckle(c, s, 83, 900, 170, 235, 2)
  blotch(c, s, 89, 30, 2, 4, [160, 245], 0.6)
}

const paintMud: PatternPainter = (c, s) => {
  // 湿った濃淡のまだら。
  fill(c, s, 190)
  blotch(c, s, 97, 70, 6, 16, [150, 165, 215], 0.5)
}

const paintAsphalt: PatternPainter = (c, s) => {
  fill(c, s, 165)
  speckle(c, s, 101, 1600, 130, 210)
}

const paintSand: PatternPainter = (c, s) => {
  fill(c, s, 240)
  speckle(c, s, 103, 2200, 220, 252, 1)
}

const paintSalt: PatternPainter = (c, s) => {
  // 砂より白く、結晶のきらつき（明点）を散らす。
  fill(c, s, 250)
  speckle(c, s, 107, 600, 232, 255, 1.5)
  speckle(c, s, 109, 80, 255, 255, 2)
}

const paintSnow: PatternPainter = (c, s) => {
  fill(c, s, 252)
  speckle(c, s, 113, 350, 240, 255, 2)
}

const paintIce: PatternPainter = (c, s) => {
  fill(c, s, 246)
  veins(c, s, 127, 4, 215, 0.5, 1.2)
}

const paintGlacier: PatternPainter = (c, s) => {
  fill(c, s, 240)
  veins(c, s, 131, 6, 190, 0.6, 2.5)
}

const paintWater: PatternPainter = (c, s) => {
  // 横うねりの波線。
  fill(c, s, 235)
  c.strokeStyle = 'rgba(180, 180, 180, 0.7)'
  c.lineWidth = 2
  for (let i = 0; i < 6; i += 1) {
    const baseY = (s / 6) * i + 8
    c.beginPath()
    for (let x = 0; x <= s; x += 4) {
      const y = baseY + Math.sin((x / s) * Math.PI * 4 + i) * 4
      if (x === 0) c.moveTo(x, y)
      else c.lineTo(x, y)
    }
    c.stroke()
  }
}

const paintFabric: PatternPainter = (c, s) => {
  // 織り目（縦横の細かいクロス）。
  fill(c, s, 235)
  c.fillStyle = 'rgb(210, 210, 210)'
  for (let i = 0; i < s; i += 4) {
    c.fillRect(i, 0, 1, s)
    c.fillRect(0, i, s, 1)
  }
}

const paintCarpet: PatternPainter = (c, s) => {
  // 細かいループの点々。布より粒立ち。
  fill(c, s, 228)
  speckle(c, s, 137, 1800, 195, 245, 2)
}

const paintLeather: PatternPainter = (c, s) => {
  // 細かい皺（短い弧）。
  fill(c, s, 232)
  const random = createRandom(139)
  c.strokeStyle = 'rgba(170, 170, 170, 0.6)'
  c.lineWidth = 1
  for (let i = 0; i < 160; i += 1) {
    const x = random() * s
    const y = random() * s
    c.beginPath()
    c.arc(x, y, 2 + random() * 4, random() * Math.PI, random() * Math.PI + 2)
    c.stroke()
  }
}

const paintPlaster: PatternPainter = (c, s) => {
  // ほぼ均質 + ごく薄いムラ（塗り跡）。
  fill(c, s, 245)
  blotch(c, s, 149, 40, 8, 20, [238, 250], 0.5)
}

const paintMetal: PatternPainter = (c, s) => {
  fill(c, s, 244)
  const random = createRandom(19)
  for (let y = 0; y < s; y += 2) {
    const shade = 225 + Math.round(random() * 28)
    c.fillStyle = `rgb(${shade}, ${shade}, ${shade})`
    c.fillRect(0, y, s, 1)
  }
}

const paintCorrodedMetal: PatternPainter = (c, s) => {
  // 錆: 金属ヘアラインの上に強い暗ブロッチ。
  paintMetal(c, s)
  blotch(c, s, 151, 70, 4, 13, [110, 140, 170], 0.6)
}

const paintDiamondPlate: PatternPainter = (c, s) => {
  // 斜めの刻み（十字の浮き出し）を格子状に。縞鋼板。
  fill(c, s, 235)
  c.strokeStyle = 'rgba(150, 150, 150, 0.9)'
  c.lineWidth = 3
  const step = s / 4
  for (let row = 0; row < 4; row += 1) {
    for (let col = 0; col < 4; col += 1) {
      const cx = col * step + step / 2 + (row % 2 === 1 ? step / 2 : 0)
      const cy = row * step + step / 2
      c.beginPath()
      c.moveTo(cx - 6, cy - 6)
      c.lineTo(cx + 6, cy + 6)
      c.stroke()
    }
  }
}

const paintFoil: PatternPainter = (c, s) => {
  // 皺くちゃのアルミ箔: ランダムな斜めハイライト。
  fill(c, s, 230)
  const random = createRandom(157)
  for (let i = 0; i < 50; i += 1) {
    const shade = 200 + Math.round(random() * 55)
    c.strokeStyle = `rgba(${shade}, ${shade}, ${shade}, 0.8)`
    c.lineWidth = 1 + random() * 3
    const x = random() * s
    const y = random() * s
    c.beginPath()
    c.moveTo(x, y)
    c.lineTo(x + (random() - 0.5) * 40, y + (random() - 0.5) * 40)
    c.stroke()
  }
}

/**
 * マテリアル → 模様の対応。無地（null 相当）は載せない:
 * Plastic / SmoothPlastic / Neon / Glass / ForceField / Air / Rubber は
 * 模様なし（質感は MATERIAL_SPECS の roughness / metalness / emissive 側で表現）。
 */
const PATTERN_PAINTERS: Readonly<
  Partial<Record<RobloxMaterial, PatternPainter>>
> = {
  Wood: paintWood,
  WoodPlanks: paintWoodPlanks,
  Cardboard: paintCardboard,
  Marble: paintMarble,
  Basalt: paintBasalt,
  Slate: paintSlate,
  CrackedLava: paintCrackedLava,
  Concrete: paintConcrete,
  Limestone: paintLimestone,
  Granite: paintGranite,
  Pavement: paintPavement,
  Brick: paintBrick,
  Pebble: paintPebble,
  Cobblestone: paintCobblestone,
  Rock: paintRock,
  Sandstone: paintSandstone,
  CorrodedMetal: paintCorrodedMetal,
  DiamondPlate: paintDiamondPlate,
  Foil: paintFoil,
  Metal: paintMetal,
  Grass: paintGrass,
  LeafyGrass: paintLeafyGrass,
  Sand: paintSand,
  Fabric: paintFabric,
  Snow: paintSnow,
  Mud: paintMud,
  Ground: paintGround,
  Asphalt: paintAsphalt,
  Salt: paintSalt,
  Ice: paintIce,
  Glacier: paintGlacier,
  Water: paintWater,
  Carpet: paintCarpet,
  CeramicTiles: paintCeramicTiles,
  ClayRoofTiles: paintClayRoofTiles,
  RoofShingles: paintRoofShingles,
  Leather: paintLeather,
  Plaster: paintPlaster,
}

/** マテリアル → 共有 canvas のキャッシュ。 */
const canvasCache = new Map<RobloxMaterial, HTMLCanvasElement>()

const getPatternCanvas = (
  material: RobloxMaterial,
): HTMLCanvasElement | null => {
  const painter = PATTERN_PAINTERS[material]
  if (!painter) {
    return null
  }
  const cached = canvasCache.get(material)
  if (cached) {
    return cached
  }
  const canvas = document.createElement('canvas')
  canvas.width = TILE_SIZE
  canvas.height = TILE_SIZE
  const context = canvas.getContext('2d')
  if (!context) {
    return null
  }
  painter(context, TILE_SIZE)
  canvasCache.set(material, canvas)
  return canvas
}

/**
 * マテリアルの模様テクスチャを生成する（無地マテリアルは null）。
 * 画像（canvas）は共有し、Texture インスタンスは呼び出しごとに新規作成する
 * （repeat はエントリ固有のため）。返した Texture の dispose は呼び出し側が行う。
 */
export const createMaterialTexture = (
  material: RobloxMaterial,
): THREE.CanvasTexture | null => {
  const canvas = getPatternCanvas(material)
  if (!canvas) {
    return null
  }
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.wrapS = THREE.RepeatWrapping
  texture.wrapT = THREE.RepeatWrapping
  return texture
}
